import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './auth';
import { spotifyService } from '../services/spotify';

// Only log in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// Debug logger function
const debugLog = (message: string, data?: any) => {
  if (isDevelopment) {
    console.log(`[SpotifyContext] ${message}`);
    if (data) console.log(data);
  }
};

// Define types for the Spotify context
interface SpotifyContextType {
  recentlyPlayed: any[];
  topTracks: any[];
  playlists: any[];
  isLoading: boolean;
  error: string | null;
  search: (query: string, types: string) => Promise<any>;
  getPlaylist: (playlistId: string) => Promise<any>;
  createPlaylist: (name: string, description?: string) => Promise<any>;
  refreshRecentlyPlayed: () => Promise<void>;
  refreshTopItems: () => Promise<void>;
  refreshPlaylists: () => Promise<void>;
}

// Create the context with default values
const SpotifyContext = createContext<SpotifyContextType>({
  recentlyPlayed: [],
  topTracks: [],
  playlists: [],
  isLoading: false,
  error: null,
  search: async () => ({}),
  getPlaylist: async () => ({}),
  createPlaylist: async () => ({}),
  refreshRecentlyPlayed: async () => {},
  refreshTopItems: async () => {},
  refreshPlaylists: async () => {},
});

// Create the provider component
export const SpotifyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, getAccessToken } = useAuth();
  const [serviceReady, setServiceReady] = useState<boolean>(false);
  const [recentlyPlayed, setRecentlyPlayed] = useState<any[]>([]);
  const [topTracks, setTopTracks] = useState<any[]>([]);
  const [playlists, setPlaylists] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize the Spotify service and load data when authenticated
  useEffect(() => {
    debugLog(`Authentication state changed, isAuthenticated = ${isAuthenticated}`);
    
    if (isAuthenticated) {
      const initService = async () => {
        try {
          const token = await getAccessToken();
          debugLog(`Access token available = ${!!token}`);
          
          if (token) {
            debugLog('Service ready, loading initial data');
            setServiceReady(true);
            setIsLoading(true);
            setError(null);
            
            // Add a small delay to ensure token is fully processed
            setTimeout(async () => {
              try {
                debugLog('Loading recently played tracks');
                const recentlyPlayedData = await spotifyService.getRecentlyPlayed();
                setRecentlyPlayed(recentlyPlayedData);
                
                debugLog('Loading top tracks');
                const topTracksData = await spotifyService.getUserTop('tracks');
                setTopTracks(topTracksData);
                
                debugLog('Loading playlists');
                const playlistsData = await spotifyService.getUserPlaylists();
                setPlaylists(playlistsData);
                
                debugLog('All data loaded successfully');
              } catch (err) {
                debugLog('Error loading initial Spotify data:', err);
                setError('Failed to load music data. Please try again.');
              } finally {
                setIsLoading(false);
              }
            }, 1000); // 1 second delay
          }
        } catch (err) {
          debugLog('Error initializing Spotify service:', err);
          setServiceReady(false);
          setError('Failed to initialize Spotify service.');
          setIsLoading(false);
        }
      };
      
      initService();
    } else {
      debugLog('User not authenticated, resetting state');
      setServiceReady(false);
      setRecentlyPlayed([]);
      setTopTracks([]);
      setPlaylists([]);
      setError(null);
    }
  }, [isAuthenticated, getAccessToken]);

  // Refresh recently played tracks
  const refreshRecentlyPlayed = async (): Promise<void> => {
    if (!serviceReady) return;
    
    try {
      const data = await spotifyService.getRecentlyPlayed();
      setRecentlyPlayed(data);
    } catch (err) {
      debugLog('Error fetching recently played tracks:', err);
      setError('Failed to load recently played tracks.');
    }
  };

  // Refresh top tracks
  const refreshTopItems = async (): Promise<void> => {
    if (!serviceReady) return;
    
    try {
      const data = await spotifyService.getUserTop('tracks');
      setTopTracks(data);
    } catch (err) {
      debugLog('Error fetching top tracks:', err);
      setError('Failed to load top tracks.');
    }
  };

  // Refresh playlists
  const refreshPlaylists = async (): Promise<void> => {
    if (!serviceReady) return;
    
    try {
      const data = await spotifyService.getUserPlaylists();
      setPlaylists(data);
    } catch (err) {
      debugLog('Error fetching playlists:', err);
      setError('Failed to load playlists.');
    }
  };

  // Search for tracks, artists, albums, or playlists
  const search = async (query: string, types: string): Promise<any> => {
    if (!serviceReady) return {};
    
    try {
      return await spotifyService.search(query, types);
    } catch (err) {
      debugLog('Error searching:', err);
      setError('Search failed. Please try again.');
      return {};
    }
  };

  // Get a specific playlist
  const getPlaylist = async (playlistId: string): Promise<any> => {
    if (!serviceReady) return {};
    
    try {
      return await spotifyService.getPlaylist(playlistId);
    } catch (err) {
      debugLog('Error fetching playlist:', err);
      setError('Failed to load playlist.');
      return {};
    }
  };

  // Create a new playlist
  const createPlaylist = async (name: string, description: string = `Created with Tunes with Otto`): Promise<any> => {
    if (!serviceReady) return {};
    
    try {
      const playlist = await spotifyService.createPlaylist(name, description);
      await refreshPlaylists(); // Refresh the playlists after creating a new one
      return playlist;
    } catch (err) {
      debugLog('Error creating playlist:', err);
      setError('Failed to create playlist.');
      return {};
    }
  };

  const value = {
    recentlyPlayed,
    topTracks,
    playlists,
    isLoading,
    error,
    search,
    getPlaylist,
    createPlaylist,
    refreshRecentlyPlayed,
    refreshTopItems,
    refreshPlaylists,
  };

  return <SpotifyContext.Provider value={value}>{children}</SpotifyContext.Provider>;
};

// Create a hook to use the Spotify context
export const useSpotify = () => useContext(SpotifyContext);
